import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { useOutletContext } from 'react-router-dom';
import Icon from '../components/Icon';
import { fetchTransactions } from '../services/api';
import { corridorMetadata, formatCurrency, timeAgo } from '../utils/format';

const STATUS_BADGE = {
  confirmed:         'badge-success',
  usdc_settled:      'badge-success',
  onramp_confirmed:  'badge-success',
  offramp_confirmed: 'badge-success',
  pending:           'badge-warning',
  initiated:         'badge-warning',
  offramp_initiated: 'badge-warning',
  failed:            'badge-danger',
};

const NETWORK_BADGE = {
  base:    { class: 'badge-info',    label: 'Base'    },
  polygon: { class: 'badge-info',    label: 'Polygon' },
  solana:  { class: 'badge-warning', label: 'Solana'  },
};

const Transactions = () => {
  const { environment } = useOutletContext();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [corridorFilter, setCorridorFilter] = useState('all');
  const [selected, setSelected] = useState(null);

  // Re-fetch whenever environment changes.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchTransactions({ environment, limit: 100 })
      .then((res) => {
        if (cancelled) return;
        setRows(res?.data || []);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || 'Failed to load transactions');
        setRows([]);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [environment]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((tx) => {
      if (statusFilter !== 'all' && tx.status !== statusFilter) return false;
      if (corridorFilter !== 'all' && tx.corridor !== corridorFilter) return false;
      if (!q) return true;
      const hash = (tx.tx_hash || '').toLowerCase();
      const sender = (tx.sender_address || '').toLowerCase();
      const recipient = (tx.receiver_address || '').toLowerCase();
      return hash.includes(q) || sender.includes(q) || recipient.includes(q);
    });
  }, [rows, query, statusFilter, corridorFilter]);

  const totalVolume = filtered.reduce((sum, t) => sum + (parseFloat(t.source_amount) || 0), 0);
  const failureRate = filtered.length
    ? (filtered.filter((t) => t.status === 'failed').length / filtered.length) * 100
    : 0;
  const avgLatency = filtered.length
    ? Math.round(filtered.reduce((s, t) => s + (parseInt(t.routing_latency_ms, 10) || 0), 0) / filtered.length)
    : 0;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Transactions</h1>
          <p>
            Live ledger across all corridors and networks
            <span className="text-tx3"> · {environment}</span>
            {!loading && <span className="text-tx3"> · {rows.length} records</span>}
          </p>
        </div>
      </div>

      <div className="dashboard-grid stagger mb-4">
        <div className="card">
          <div className="card-title">Filtered Volume</div>
          <div className="stat-value">{formatCurrency(totalVolume, { compact: true })}</div>
          <div className="stat-label">{filtered.length} transactions</div>
        </div>
        <div className="card">
          <div className="card-title">Failure Rate</div>
          <div className="stat-value" style={{ color: failureRate > 5 ? 'var(--err)' : 'var(--ok)' }}>
            {failureRate.toFixed(1)}%
          </div>
          <div className="stat-label">In current view</div>
        </div>
        <div className="card">
          <div className="card-title">Average Latency</div>
          <div className="stat-value">
            {avgLatency}<span className="text-tx3 text-md" style={{ marginLeft: 4 }}>ms</span>
          </div>
          <div className="stat-label">End-to-end</div>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-title">Filter</div>
        <div className="row row-gap-3" style={{ flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: '1 1 240px', minWidth: 200 }}>
            <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--tx3)' }}>
              <Icon name="search" size={16} />
            </span>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by hash, sender, recipient..."
              aria-label="Search transactions"
              style={{
                width: '100%',
                padding: '10px 14px 10px 36px',
                background: 'var(--bg-elevated)',
                border: '1px solid var(--bd)',
                borderRadius: 'var(--r-md)',
                color: 'var(--tx1)',
                fontSize: 13,
                outline: 'none',
              }}
            />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} aria-label="Filter by status" style={selectStyle}>
            <option value="all">All statuses</option>
            <option value="usdc_settled">Settled</option>
            <option value="onramp_confirmed">On-ramp confirmed</option>
            <option value="offramp_confirmed">Off-ramp confirmed</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
          </select>
          <select value={corridorFilter} onChange={(e) => setCorridorFilter(e.target.value)} aria-label="Filter by corridor" style={selectStyle}>
            <option value="all">All corridors</option>
            {Object.entries(corridorMetadata).map(([code, meta]) => (
              <option key={code} value={code}>{meta.flag} {code}</option>
            ))}
          </select>
          {(query || statusFilter !== 'all' || corridorFilter !== 'all') && (
            <button type="button" className="btn btn-ghost"
              onClick={() => { setQuery(''); setStatusFilter('all'); setCorridorFilter('all'); }}>
              Clear
            </button>
          )}
        </div>
      </div>

      <div className="card">
        <div className="row mb-3" style={{ justifyContent: 'space-between' }}>
          <div className="card-title" style={{ margin: 0 }}>Ledger</div>
          <span className="text-tx3 text-xs">{filtered.length} of {rows.length} shown</span>
        </div>

        {error && (
          <div role="alert" style={{ background: 'var(--err-bg)', color: 'var(--err)', padding: 'var(--s3)', borderRadius: 'var(--r-md)', fontSize: 13, marginBottom: 'var(--s3)' }}>
            {error}
          </div>
        )}

        {loading && rows.length === 0 ? (
          <div className="col col-gap-3">
            {[0, 1, 2, 3].map((i) => <div key={i} className="skeleton skeleton-block" style={{ height: 48 }} />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="empty">
            <div className="empty-icon"><Icon name="search" size={26} /></div>
            <h3>{rows.length === 0 ? 'No transactions yet' : 'No transactions match'}</h3>
            <p>
              {rows.length === 0
                ? `No transactions on ${environment}. Once webhooks fire from Alchemy / Paytrie / Yellow Card, they'll appear here.`
                : 'Try adjusting your filters or clearing the search.'}
            </p>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Tx Hash</th>
                  <th>Direction</th>
                  <th>Corridor</th>
                  <th>Network</th>
                  <th style={{ textAlign: 'right' }}>Amount (CAD)</th>
                  <th style={{ textAlign: 'right' }}>Latency</th>
                  <th>Status</th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((tx) => {
                  const isOpen = selected === tx.id;
                  const network = NETWORK_BADGE[tx.network] || { class: 'badge-muted', label: tx.network || '—' };
                  return (
                    <React.Fragment key={tx.id}>
                      <tr onClick={() => setSelected(isOpen ? null : tx.id)} style={{ cursor: 'pointer' }}>
                        <td style={{ color: 'var(--tx1)', fontWeight: 600, fontFamily: 'var(--mono)' }}>
                          {(tx.tx_hash || '—').slice(0, 18)}{tx.tx_hash?.length > 18 ? '…' : ''}
                        </td>
                        <td>
                          <span className={clsx('badge', tx.type === 'on_ramp' ? 'badge-info' : 'badge-muted')}>
                            {tx.type?.replace('_', '-') || '—'}
                          </span>
                        </td>
                        <td>
                          {tx.corridor ? (
                            <>{corridorMetadata[tx.corridor]?.flag} {tx.corridor}</>
                          ) : '—'}
                        </td>
                        <td>
                          <span className={clsx('badge', network.class)}>{network.label}</span>
                        </td>
                        <td style={{ textAlign: 'right', color: 'var(--tx1)', fontWeight: 600 }}>
                          {tx.source_amount != null ? formatCurrency(tx.source_amount, { compact: false }) : '—'}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {tx.routing_latency_ms != null ? `${tx.routing_latency_ms}ms` : '—'}
                        </td>
                        <td>
                          <span className={clsx('badge', STATUS_BADGE[tx.status] || 'badge-muted')}>
                            {tx.status || '—'}
                          </span>
                        </td>
                        <td>{timeAgo(tx.initiated_at || tx.created_at)}</td>
                      </tr>
                      {isOpen && (
                        <tr style={{ background: 'var(--bg-elevated)' }}>
                          <td colSpan={8} style={{ padding: 'var(--s4)' }}>
                            <div className="row row-gap-4" style={{ flexWrap: 'wrap' }}>
                              <DetailCol label="Sender"        value={tx.sender_address}   mono />
                              <DetailCol label="Recipient"     value={tx.receiver_address} mono />
                              <DetailCol label="USDC Settled"  value={tx.usdc_amount != null ? formatCurrency(tx.usdc_amount, { compact: false, currency: 'USD' }) : '—'} accent="var(--teal)" />
                              <DetailCol label="Gas (USD)"     value={tx.gas_cost_usd != null ? `$${parseFloat(tx.gas_cost_usd).toFixed(4)}` : '—'} />
                              <DetailCol label="Route"         value={tx.route_selected || '—'} />
                              <DetailCol label="Created"       value={tx.initiated_at ? new Date(tx.initiated_at).toLocaleString() : '—'} />
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

const DetailCol = ({ label, value, mono, accent }) => (
  <div className="col col-gap-2">
    <div className="text-tx3 text-xs">{label}</div>
    <div style={{
      fontFamily: mono ? 'var(--mono)' : 'inherit',
      fontSize: mono ? 12 : 13,
      color: accent || 'var(--tx1)',
      fontWeight: accent ? 700 : 500,
    }}>{value || '—'}</div>
  </div>
);

const selectStyle = {
  padding: '10px 14px',
  background: 'var(--bg-elevated)',
  border: '1px solid var(--bd)',
  borderRadius: 'var(--r-md)',
  color: 'var(--tx1)',
  fontSize: 13,
  fontFamily: 'var(--font)',
  cursor: 'pointer',
  outline: 'none',
};

export default Transactions;
